/**
 * Help Request Domain
 * A player asks the moderator for help — for themselves, or for the team — and
 * can take the request back; the moderator resolves it from the portal.
 *
 * The server holds the request (help-request.go) and answers every send, to the
 * player alone: a request on OpCodeRequestHelpSuccess or OpCodeRequestHelpFailed,
 * a cancel on OpCodeCancelHelpSuccess. Until it answers, the send is on its way
 * (`sending`), which the menu shows on the choice that was pressed. A send that
 * never reaches the server — no connection, or the match in its end lobby —
 * ends its wait and says so; one turned away for another command on its way
 * ends its wait, and the page top says why. What failed is kept as a code,
 * worded in the player's language as the menu draws it.
 * Follows the project's unidirectional data flow pattern.
 */

import { create } from 'zustand';
import { OpCode, MessageType } from '../constants/op-codes';
import i18n from '../i18n/config';
import { sendGatedCommand, type GatedRequest, type NotSentReason } from './command-gate';
import { showInfoToast } from './toasts';

// ==================== TYPES ====================

/** Help for the player who asks, or for their whole team. */
export type HelpRequestType = 'individual' | 'cohort';

/** What can be on its way to the server: a request of a kind, or a cancel. */
type HelpRequestSend = HelpRequestType | 'cancel';

/** Why a send did not go through: the server refused it, or it never reached the server (the gate's reason). */
export type HelpRequestFailure = 'refused' | NotSentReason;

interface HelpRequestState {
  /** The request the server holds for the player, if any. */
  activeRequest: HelpRequestType | null;
  /** What is on its way, until the server answers it. */
  sending: HelpRequestSend | null;
  /** Why the last send did not go through, until the next send. */
  error: HelpRequestFailure | null;
  /** Forget it all: the player has left the match (connection-reset.ts). */
  reset: () => void;
}

// ==================== STORE ====================

export const useHelpRequestStore = create<HelpRequestState>((set) => ({
  activeRequest: null,
  sending: null,
  error: null,
  reset: () => set({ activeRequest: null, sending: null, error: null }),
}));

function isHelpRequestType(value: unknown): value is HelpRequestType {
  return value === 'individual' || value === 'cohort';
}

// ==================== HANDLERS ====================

/**
 * Handler for request_help_success message
 * The server holds the request.
 */
export function handleRequestHelpSuccess(data: { help_type?: string }) {
  useHelpRequestStore.setState({
    activeRequest: isHelpRequestType(data.help_type) ? data.help_type : null,
    sending: null,
    error: null,
  });
}

/**
 * Handler for request_help_failed message
 * The server refused the request. Its reason is English for the console; the
 * player reads that it did not reach the moderator.
 */
export function handleRequestHelpFailed(data: { error: string }) {
  console.warn('[HelpRequest] The server refused the help request:', data.error);
  useHelpRequestStore.setState({ sending: null, error: 'refused' });
}

/**
 * Handler for cancel_help_success message
 * Called when server confirms help request cancellation
 */
export function handleCancelHelpSuccess() {
  useHelpRequestStore.setState({ activeRequest: null, sending: null, error: null });
}

/**
 * Handler for help_request_resolved message
 * The moderator marked the player's request resolved in the portal. The menu
 * is most likely closed by then, so the player is told in a toast. A cancel on
 * its way still waits for its own answer.
 */
export function handleHelpRequestResolved() {
  const { activeRequest } = useHelpRequestStore.getState();
  useHelpRequestStore.setState({ activeRequest: null, error: null });
  if (activeRequest) {
    showInfoToast(i18n.t('menu.helpRequest.resolved'), i18n.t('menu.helpRequest.resolvedNote'));
  }
}

/**
 * The request the server holds for the player, from the players list it sends
 * on joining and after changes (players.ts): the menu shows what the server
 * holds and nothing else. A rejoin starts the player afresh on the server, so
 * a request held before a dropped connection goes.
 */
export function syncHelpRequestFromServer(helpType: string | undefined) {
  const activeRequest = isHelpRequestType(helpType) ? helpType : null;
  if (useHelpRequestStore.getState().activeRequest !== activeRequest) {
    useHelpRequestStore.setState({ activeRequest });
  }
}

// ==================== SERVICE METHODS ====================

/**
 * Sends a help request or a cancel through the command gate, holding `send`
 * as what is on its way until the server answers. The gate ends the wait
 * without an answer — the send turned away for another command on its way,
 * or lost with the socket — and a send that cannot go at all keeps the gate's
 * reason, which the menu says where it says a refusal.
 */
async function sendHelp(send: HelpRequestSend, opCode: GatedRequest, message: object): Promise<void> {
  if (useHelpRequestStore.getState().sending) return;
  useHelpRequestStore.setState({ sending: send, error: null });

  await sendGatedCommand(opCode, JSON.stringify(message), {
    endWait: () => {
      if (useHelpRequestStore.getState().sending === send) useHelpRequestStore.setState({ sending: null });
    },
    notSent: (_refusal, reason) => useHelpRequestStore.setState({ sending: null, error: reason }),
  });
}

/**
 * Request help from moderator
 * @param type - Type of help: 'individual' or 'cohort'
 */
export function requestHelp(type: HelpRequestType): Promise<void> {
  return sendHelp(type, OpCode.OpCodeRequestHelp, { type: MessageType.MessageTypeRequestHelp, help_type: type });
}

/**
 * Cancel active help request
 */
export function cancelHelp(): Promise<void> {
  return sendHelp('cancel', OpCode.OpCodeCancelHelp, { type: MessageType.MessageTypeCancelHelp });
}
